var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { getAsAttributeSelector, getEventFieldsForAnchorClick, getEventFieldsForNonLinkClicks, getTemplateString, isValidLink, onAddEventListener } from '../utils/plugin-utils';
import { ANCHOR_TAG, BUTTON_TAG, INPUT_BUTTON_TAG, INPUT_SUBMIT_TAG, JS_CLICK_EVENT_STRING } from '../constants/plugin-constants';
import BasePlugin from './base';
import { removeEventListeners } from '../utils/event-listener-utils';
export var DEFAULT_CLICK_PLUGIN_CONFIG_VALUES = {
    parentElementSelector: 'body',
    eventContentIdAttr: 'data-awsma-event-content-id',
    ignoreAttr: 'data-awsma-ignore',
    eventNameAttr: 'data-awsma-event-name',
    eventContentTitleAttr: 'data-awsma-event-content-title',
    eventValueAttr: 'data-awsma-event-value',
};
/**
 * The DefaultClickPlugin is responsible for handling the out-of-the-box click tracking with/without a minimal config input.
 * It, by default, handles click events for all `a`, `button`, and the target elements (if any) specified as a selector string
 * in eventContentId config field.
 *
 * The parentElementSelector field in the config narrows down the tracking. For eg. providing 'body' as a value will track all 'a', 'button', and eventContentId
 * elements within the body. Providing '.small-section', will track all 'a', 'button', and eventContentId elements within that alone.
 *
 * The ignore field in the config lets you ignore some of the elements even though they are within the same parentElementSelector. You can pass the
 * data attribute name or any other attribute name to ignore this element from tracking.
 *
 * Any selector that is passed as a string will be fed into the document.querySelector(selectorString) (or) document.querySelectorAll(selectorString)
 * depending on the requirement.
 *
 * NOTE: You cannot disable the tracking of a few elements with this plugin.
 * You can either have all trackings (a, button, target (if specified)) or none at all (by disabling the entire plugin itself).
 *
 * @class DefaultClickPlugin
 * @extends {BasePlugin<DefaultClickPluginConfigType>}
 * @implements {IDefaultClickPlugin<T>}
 * @template T=DefaultClickPluginConfigType
 */
var DefaultClickPlugin = /** @class */ (function (_super) {
    __extends(DefaultClickPlugin, _super);
    function DefaultClickPlugin() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        /**
         * This method gets called whenever any specified event occurs.
         *
         * @protected
         * @param {MouseEvent} event
         * @memberof DefaultClickPlugin
         */
        _this.handleClickEventCallback = function (event) {
            var selectors = Object.keys(_this.selectorsToTrack);
            var target = event.target;
            for (var i = 0; i < selectors.length; ++i) {
                var selector = selectors[i];
                if (target.closest(selector)) {
                    _this.selectorsToTrack[selector](event); // Call the callback method of the selector
                    break; // We shall stop the loop as we find the first matching selector.
                }
            }
        };
        return _this;
    }
    DefaultClickPlugin.prototype.init = function (config) {
        var _a;
        _super.prototype.init.call(this, config);
        // Merge the default values with the passed config.
        this.config = __assign(__assign({}, DEFAULT_CLICK_PLUGIN_CONFIG_VALUES), config);
        var buttonCallback = this.handleButtonClickEvent.bind(this);
        // Maintain a map of selectors to their callback methods.
        this.selectorsToTrack = (_a = {},
            _a[ANCHOR_TAG] = this.handleAnchorClickEvent.bind(this),
            _a[BUTTON_TAG] = buttonCallback,
            _a[INPUT_BUTTON_TAG] = buttonCallback,
            _a[INPUT_SUBMIT_TAG] = buttonCallback,
            _a[getAsAttributeSelector(this.config.eventContentIdAttr)] = this.handleTargetElementClickEvent.bind(this),
            _a);
        try {
            // Set parent element
            this.parentElement = document.querySelector(this.config.parentElementSelector);
            // Start tracking
            this.startDefaultClickTracking();
        }
        catch (err) {
            this.logger.error(err);
        }
    };
    DefaultClickPlugin.prototype.startDefaultClickTracking = function () {
        this.logger.log(getTemplateString('Default Click Tracking Started for {0}, {1}, and {2} selectors.')(ANCHOR_TAG, BUTTON_TAG, this.config.eventContentIdAttr));
        this.attachEventListenerToTheParentElement();
    };
    DefaultClickPlugin.prototype.destroy = function () {
        _super.prototype.destroy.call(this);
        removeEventListeners(this.pluginName);
    };
    /**
     *
     * Handles the click event of anchor tags.
     *
     * @private
     * @param {MouseEvent} event
     * @memberof DefaultClickPlugin
     */
    DefaultClickPlugin.prototype.handleAnchorClickEvent = function (event) {
        var htmlElement = event.target;
        var anchorElement = htmlElement.closest(ANCHOR_TAG);
        // Only track the actual links.
        if (isValidLink(anchorElement)) {
            this.pushEventToDataLayer(getEventFieldsForAnchorClick(htmlElement, anchorElement, this.config.eventContentIdAttr));
        }
    };
    /**
     * Handles the click event of button tags.
     * This is similar to the `handleTargetElementClickEvent` method but intentionally keeping the method separate
     * in case we want to handle specific things for button clicks. Otherwise, both can be merged to a single function.
     *
     * @private
     * @param {MouseEvent} event
     * @memberof DefaultClickPlugin
     */
    DefaultClickPlugin.prototype.handleButtonClickEvent = function (event) {
        // Matches <button>, <input type="button" />, and <input type="submit" />
        var buttonElement = event.target;
        this.pushEventToDataLayer(getEventFieldsForNonLinkClicks(buttonElement, this.config));
    };
    /**
     *
     * Handles the click event of specified target elements (if any).
     *
     * @private
     * @param {MouseEvent} event
     * @memberof DefaultClickPlugin
     */
    DefaultClickPlugin.prototype.handleTargetElementClickEvent = function (event) {
        var htmlElement = event.target;
        this.pushEventToDataLayer(getEventFieldsForNonLinkClicks(htmlElement, this.config));
    };
    /**
     * Adds the event listener to the specified parentElement (defaults to document.body)
     * With the help of event delegation, the respective elements/selectors are validated if they
     * are clicked.
     *
     * @private
     * @memberof DefaultClickPlugin
     */
    DefaultClickPlugin.prototype.attachEventListenerToTheParentElement = function () {
        // Add event listeners for the selectors
        onAddEventListener(this.parentElement, JS_CLICK_EVENT_STRING, {
            callback: this.handleClickEventCallback,
            ignoreSelector: this.config.ignoreAttr,
            pluginName: this.pluginName,
        });
    };
    return DefaultClickPlugin;
}(BasePlugin));
export default DefaultClickPlugin;
//# sourceMappingURL=data:application/json;base64,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